export interface PushTemplatePresetButton {
  id: string;
  label: string;
  click_action?: string;
}

export interface PushTemplatePreset {
  id: string;
  icon: string;
  name: string;
  description: string;
  template: {
    template_type: string;
    name: string;
    description?: string;
    title: string;
    body: string;
    image_url?: string;
    click_action?: string;
    category?: string;
    buttons?: PushTemplatePresetButton[];
  };
  companion_group_preset?: string;
}

export interface IosGroupPresetAction {
  id: string;
  label: string;
  destructive?: boolean;
  opens_app?: boolean;
}

export interface IosGroupPreset {
  category: string;
  label: string;
  description: string;
  actions: IosGroupPresetAction[];
}

export const IOS_GROUP_PRESETS: IosGroupPreset[] = [
  {
    category: "reminder",
    label: "Reminder (Start / Snooze / Skip)",
    description:
      "Classic time-based reminder. Use for workouts, meditations, daily tasks, scheduled prompts.",
    actions: [
      { id: "start_now", label: "Start now", opens_app: true },
      { id: "snooze_15", label: "Snooze 15 min" },
      { id: "skip_today", label: "Skip", destructive: true },
    ],
  },
  {
    category: "confirm",
    label: "Confirm action (Confirm / Dismiss)",
    description:
      "Yes/no decisions: appointment confirmations, opt-in prompts, terms acceptance.",
    actions: [
      { id: "confirm", label: "Confirm", opens_app: true },
      { id: "dismiss", label: "Dismiss" },
    ],
  },
  {
    category: "invitation",
    label: "Invitation (Accept / Decline)",
    description:
      "Team invites, event RSVPs, friend requests, group join prompts.",
    actions: [
      { id: "accept", label: "Accept", opens_app: true },
      { id: "decline", label: "Decline", destructive: true },
    ],
  },
  {
    category: "message",
    label: "Message (Open / Mark read)",
    description:
      "Inbound chat, DM, support reply. Mark-read is a background action.",
    actions: [
      { id: "open_message", label: "Open", opens_app: true },
      { id: "mark_read", label: "Mark read" },
    ],
  },
  {
    category: "engagement",
    label: "Re-engagement (Open / Mute)",
    description:
      "Inactivity nudges, content recommendations, feature highlights.",
    actions: [
      { id: "open", label: "Open", opens_app: true },
      { id: "mute", label: "Mute these" },
    ],
  },
];

export const PUSH_TEMPLATE_PRESETS: PushTemplatePreset[] = [
  {
    id: "welcome",
    icon: "👋",
    name: "Welcome",
    description: "First-touch greeting after signup or first login.",
    template: {
      template_type: "welcome",
      name: "Welcome",
      description: "Sent the first time a user signs up or returns after a long absence.",
      title: "Welcome to {{ $anythink.trigger.data.tenant_name }}",
      body: "Hi {{ $anythink.trigger.data.first_name }} — let's get you set up.",
      click_action: "/home",
    },
  },
  {
    id: "reminder",
    icon: "⏰",
    name: "Reminder with Snooze/Skip",
    description: "Time-based prompt with three native action buttons.",
    template: {
      template_type: "workout_reminder",
      name: "Workout reminder",
      description: "Daily training reminder. Snooze re-queues 15 min later; skip cancels today's reminder.",
      title: "Time to {{ $anythink.trigger.data.activity }}",
      body: "Your session is ready when you are.",
      click_action: "/workout/{{ $anythink.trigger.data.workout_id }}",
      category: "reminder",
      buttons: [
        { id: "start_now", label: "Start now", click_action: "/workout/{{ $anythink.trigger.data.workout_id }}" },
        { id: "snooze_15", label: "Snooze 15 min" },
        { id: "skip_today", label: "Skip" },
      ],
    },
    companion_group_preset: "reminder",
  },
  {
    id: "confirm",
    icon: "✅",
    name: "Confirmation prompt",
    description: "Confirm/Dismiss decision — appointments, opt-ins, RSVPs.",
    template: {
      template_type: "confirm_action",
      name: "Confirmation",
      description: "Time-sensitive yes/no — confirm opens the app to the decision context; dismiss is silent.",
      title: "{{ $anythink.trigger.data.subject }}",
      body: "{{ $anythink.trigger.data.body_text }}",
      click_action: "/action/{{ $anythink.trigger.data.action_id }}",
      category: "confirm",
      buttons: [
        { id: "confirm", label: "Confirm", click_action: "/action/{{ $anythink.trigger.data.action_id }}/confirm" },
        { id: "dismiss", label: "Dismiss" },
      ],
    },
    companion_group_preset: "confirm",
  },
  {
    id: "invitation",
    icon: "🎉",
    name: "Invitation",
    description: "Team invites, event RSVPs, friend / group requests.",
    template: {
      template_type: "invitation",
      name: "Invitation",
      description: "Accept opens the app to the inviting record; decline marks declined server-side.",
      title: "{{ $anythink.trigger.data.inviter_name }} invited you",
      body: "{{ $anythink.trigger.data.body_text }}",
      click_action: "/invitations/{{ $anythink.trigger.data.invitation_id }}",
      category: "invitation",
      buttons: [
        { id: "accept", label: "Accept", click_action: "/invitations/{{ $anythink.trigger.data.invitation_id }}" },
        { id: "decline", label: "Decline" },
      ],
    },
    companion_group_preset: "invitation",
  },
  {
    id: "message",
    icon: "💬",
    name: "New message",
    description: "Inbound chat or DM with quick Open / Mark-read.",
    template: {
      template_type: "new_message",
      name: "New message",
      description: "Open routes to the conversation; mark-read is a silent background action.",
      title: "{{ $anythink.trigger.data.sender_name }}",
      body: "{{ $anythink.trigger.data.preview }}",
      click_action: "/messages/{{ $anythink.trigger.data.conversation_id }}",
      category: "message",
      buttons: [
        { id: "open_message", label: "Open", click_action: "/messages/{{ $anythink.trigger.data.conversation_id }}" },
        { id: "mark_read", label: "Mark read" },
      ],
    },
    companion_group_preset: "message",
  },
  {
    id: "engagement",
    icon: "🔥",
    name: "Re-engagement",
    description: "Lapsed-user nudge with Open / Mute opt-out.",
    template: {
      template_type: "re_engagement",
      name: "Re-engagement",
      description: "Sent to users who haven't opened the app in a while. Mute prevents future re-engagement pushes.",
      title: "{{ $anythink.trigger.data.title }}",
      body: "{{ $anythink.trigger.data.body_text }}",
      click_action: "/home",
      category: "engagement",
      buttons: [
        { id: "open", label: "Open" },
        { id: "mute", label: "Mute these" },
      ],
    },
    companion_group_preset: "engagement",
  },
  {
    id: "blank",
    icon: "✏️",
    name: "Blank",
    description: "Start from scratch — empty fields, no buttons.",
    template: {
      template_type: "",
      name: "",
      title: "",
      body: "",
    },
  },
];
